import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import React from 'react'

import { type IconProp } from 'slab/types'

export interface BaseIconButtonProps {
  children: React.ReactNode;
  icon: IconProp;
  /**
   * The accessible name for the button, which will be read by screen readers.
   *
   * This is required because icon buttons don't have any visible text. Sighted
   * users can derive the button's purpose visually, but we need to provide
   * text for screen readers to announce.
   */
  label: string;
  /**
   * Indicates whether the button is selected or active. This is useful for
   * buttons that trigger dropdown menus or popovers.
   */
  selected?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'destructive' | 'primary' | 'secondary';
}

const buttonClassNames = cva(
  'appearance-none flex shrink-0 items-center justify-center rounded-lg transition outline outline-offset-2 outline-2 outline-transparent focus-visible:outline-thatch-500 aria-disabled:pointer-events-none',
  {
    variants: {
      size: {
        small: 'w-6 h-6',
        medium: 'w-8 h-8',
        large: 'w-10 h-10',
      },
      variant: {
        default:
          'text-gray-500 hover:text-gray-600 disabled:text-gray-800/40 aria-disabled:text-gray-800/40 bg-transparent hover:bg-gray-100 data-[selected="true"]:bg-gray-100 data-[state*="open"]:bg-gray-100 focus-visible:bg-gray-100 active:bg-gray-50 disabled:bg-transparent aria-disabled:bg-transparent',
        destructive:
          'text-red-500 hover:text-red-600 disabled:text-red-400 aria-disabled:text-red-400 bg-transparent hover:bg-red-100 data-[selected="true"]:bg-red-100 data-[state*="open"]:bg-red-100 focus-visible:bg-red-100 active:bg-red-50 disabled:bg-transparent aria-disabled:bg-transparent',
        primary:
          'text-white hover:text-white bg-gradient-to-b from-black/20 disabled:bg-none aria-disabled:bg-none shadow-[0_1px_0_1px_rgba(255,255,255,.25)_inset] active:shadow-[0_1px_0_0_rgba(255,255,255,.25)_inset] disabled:shadow-none aria-disabled:shadow-none border-thatch-600 border border-b-2 hover:border-thatch-700 data-[selected="true"]:border-thatch-700 data-[state*="open"]:border-thatch-700 focus-visible:border-thatch-700 disabled:border-thatch-300 aria-disabled:border-thatch-300 bg-thatch-500 hover:bg-thatch-600 data-[selected="true"]:bg-thatch-600 data-[state*="open"]:bg-thatch-600 focus-visible:bg-thatch-600 disabled:bg-thatch-300 aria-disabled:bg-thatch-300',
        secondary:
          'text-gray-800 hover:text-gray-800 disabled:text-gray-800/40 aria-disabled:text-gray-800/40 border-2 border-transparent disabled:border-gray-200 aria-disabled:border-gray-200 bg-white bg-clip-padding disabled:bg-clip-border aria-disabled:bg-clip-border hover:bg-gray-100 data-[selected="true"]:bg-gray-100 data-[state*="open"]:bg-gray-100 focus-visible:bg-gray-100 active:bg-gray-50 disabled:bg-gray-200 aria-disabled:bg-gray-200 disabled:border-gradient-none aria-disabled:border-gradient-none border-gradient-to-b from-gray-200 to-gray-400 before:-inset-[2px]',
      },
    },
  }
)

export function BaseIconButton ({
  children,
  icon: Icon,
  label,
  selected = false,
  size = 'medium',
  variant = 'default',
  ...props
}: BaseIconButtonProps) {
  const className = buttonClassNames({ size, variant })

  return (
    <Slot
      {...props}
      aria-label={label}
      data-selected={selected}
      className={className}
    >
      <Slottable>
        {children}
      </Slottable>
      <Icon size={16} aria-hidden />
    </Slot>
  )
}
